import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Card,
  Icon,
  InputAdornment,
  alpha,
  Theme,
} from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDInput from 'src/components/md/MDInput';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import SearchIcon from '@mui/icons-material/Search';

import useCustomers, {
  CustomerPreviewDTO,
} from 'src/api/accounts-receivable/customers/useCustomers';
import MDTypography from 'src/components/md/MDTypography';
import CustomerPill from 'src/components/customer/CustomerPill';
import CustomerModal from 'src/components/customer/CustomerModal';

const blankPlaceholder = (
  <MDBox display="flex" alignItems="center">
    -
  </MDBox>
);

const CustomerTable: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const search = useSearch({ from: '/authenticated/customers' });
  const navigate = useNavigate({ from: '/customers' });
  const selectedCustomerId = search.customerId;

  const { data, isLoading, isFetching, error } = useCustomers({
    page: pageIndex,
    pageSize: pageSize,
    name: searchQuery.length > 0 ? searchQuery : undefined,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPageIndex(1);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    // MUI pagination is 0-based, but our API is 1-based
    setPageIndex(newPage + 1);
  };

  const columns: ColumnDef<CustomerPreviewDTO>[] = [
    {
      accessorKey: 'index',
      header: '#',
      cell: (info) => {
        const rowIndex = info.row.index;
        const absoluteIndex = (pageIndex - 1) * pageSize + rowIndex + 1;
        return <MDTypography variant="caption">{absoluteIndex}</MDTypography>;
      },
      size: 50,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 400,
      cell: (info) => {
        const customer = info.row.original;
        return (
          <CustomerPill
            customer={{
              name: customer.name,
              erpDisplayName: customer.erpDisplayName,
            }}
            showERPDisplayName={true}
          />
        );
      },
    },
    {
      accessorKey: 'aliases',
      header: 'Aliases',
      size: 300,
      cell: (info) => {
        const aliases = info.getValue() as string[] | null;
        if (!aliases || aliases.length === 0) {
          return blankPlaceholder;
        }
        return (
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {aliases.map((alias, index) => (
              <MDBox
                key={index}
                sx={{
                  backgroundColor: 'rgba(26, 115, 232, 0.08)',
                  color: 'info.main',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '16px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {alias}
              </MDBox>
            ))}
          </MDBox>
        );
      },
    },
    {
      accessorKey: 'address',
      header: 'Address',
      cell: (info) => {
        const value = info.getValue() as string;
        return value ? (
          <MDTypography variant="caption">{value}</MDTypography>
        ) : (
          blankPlaceholder
        );
      },
    },
    {
      accessorKey: 'emailDomain',
      header: 'Email Domain',
      cell: (info) => {
        const value = info.getValue() as string;
        return value ? (
          <MDTypography variant="caption">{value}</MDTypography>
        ) : (
          blankPlaceholder
        );
      },
    },
    {
      accessorKey: 'contactPerson',
      header: 'Contact Person',
      cell: (info) => {
        const value = info.getValue() as string;
        return value ? (
          <MDTypography variant="caption">{value}</MDTypography>
        ) : (
          blankPlaceholder
        );
      },
    },
    {
      accessorKey: 'paymentTerms',
      header: 'Payment Terms',
      cell: (info) => {
        const value = info.getValue() as string;
        return value ? (
          <MDTypography variant="caption">{value}</MDTypography>
        ) : (
          blankPlaceholder
        );
      },
    },
    {
      accessorKey: 'creditLimit',
      header: 'Credit Limit',
      cell: (info) => {
        const value = info.getValue();
        const currency = info.row.original.creditLimitCurrency;

        // Check if value is missing or invalid
        if (value === null || value === undefined || isNaN(Number(value))) {
          return blankPlaceholder;
        }

        if (!currency) {
          return (
            <MDBox display="flex" alignItems="center">
              <Icon sx={{ mr: '5px' }} color="error" fontSize="small">
                error
              </Icon>
              <MDTypography variant="caption" color="error">
                Missing currency
              </MDTypography>
            </MDBox>
          );
        }

        const formatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(Number(value));

        return <MDTypography variant="caption">{formatted}</MDTypography>;
      },
    },
  ];

  const table = useReactTable({
    data: data?.edges || [],
    columns,
    pageCount: data?.pageInfo?.totalPages || -1,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex - 1,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({ pageIndex: pageIndex - 1, pageSize })
          : updater;
      setPageIndex(next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (isLoading) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (error) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <ErrorMessage error={error} />
      </MDBox>
    );
  }

  return (
    <Card sx={{ p: 2, position: 'relative' }}>
      {isFetching && (
        <MDBox
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </MDBox>
      )}
      <MDBox>
        <MDBox pb={2} sx={{ maxWidth: '600px' }}>
          <MDInput
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleSearchChange(e)
            }
            fullWidth
            placeholder="Search customers by name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: (theme: Theme) =>
                  alpha(theme.palette.background.default, 0.8),
                '&:hover': {
                  backgroundColor: (theme: Theme) =>
                    alpha(theme.palette.background.default, 0.95),
                },
                '&.Mui-focused': {
                  backgroundColor: 'white',
                },
              },
            }}
          />
        </MDBox>
        <TableContainer sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow>
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        position: 'relative',
                        textAlign: 'left',
                        opacity: 0.7,
                        background: 'transparent',
                        color: 'rgb(123, 128, 154)',
                        boxShadow: 'none',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        width: `${header.column.columnDef.size || 'auto'}px`,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : header.column.columnDef.header instanceof Function
                          ? header.column.columnDef.header(header.getContext())
                          : header.column.columnDef.header}
                    </TableCell>
                  )),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  onClick={() => {
                    navigate({
                      to: '.',
                      search: { customerId: row.original.id, tab: 'orders' },
                    });
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        color: '#7b809a',
                        fontSize: '0.875rem',
                        fontWeight: '400',
                      }}
                    >
                      {cell.column.columnDef.cell instanceof Function
                        ? cell.column.columnDef.cell(cell.getContext())
                        : String(cell.getValue() ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.pageInfo?.totalCount || 0}
          page={pageIndex - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPageIndex(1);
          }}
          nextIconButtonProps={{
            disabled: !data?.pageInfo?.hasNextPage,
          }}
          backIconButtonProps={{
            disabled: !data?.pageInfo?.hasPreviousPage,
          }}
        />
      </MDBox>

      {selectedCustomerId && (
        <CustomerModal
          customerId={selectedCustomerId}
          onClose={() => {
            navigate({ to: '.', search: {} });
          }}
        />
      )}
    </Card>
  );
};

export default CustomerTable;
